import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Navigate, Link } from 'react-router-dom';
import {
  ArrowLeft, Loader2, Save, RotateCcw, Eye, FileText, CheckCircle2,
  AlertTriangle, Copy, Palette, Image as ImageIcon, Upload, X,
} from 'lucide-react';
import { toast } from 'sonner';
import { useAdminAuth } from './AdminAuthContext';
import { adminApi } from './adminApi';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';

const PALETTE_FIELDS = [
  { key: 'gradient_from',  label: 'Gradient start',  hint: 'Top-left of the hero banner' },
  { key: 'gradient_to',    label: 'Gradient end',    hint: 'Bottom-right of the hero banner' },
  { key: 'accent_color',   label: 'Accent',          hint: 'Chip labels, left-borders, section headings' },
  { key: 'button_color',   label: 'Button',          hint: 'Primary CTA fill (invite button etc.)' },
  { key: 'text_on_accent', label: 'Text on accent',  hint: 'Font color on top of gradient/button' },
];

export function AdminEmailTemplatesPage() {
  const { admin, loading } = useAdminAuth();
  const [templates, setTemplates] = useState([]);
  const [activeKey, setActiveKey] = useState(null);
  const [tpl, setTpl] = useState(null);
  const [subject, setSubject] = useState('');
  const [plainBody, setPlainBody] = useState('');
  const [htmlBody, setHtmlBody] = useState('');
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [fetching, setFetching] = useState(true);
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [sample, setSample] = useState({});          // editable preview sample
  const [palette, setPalette] = useState(null);
  const [savingPalette, setSavingPalette] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const logoRef = useRef(null);

  const refreshList = useCallback(async () => {
    setFetching(true);
    try {
      const r = await adminApi.get('/admin/email-templates');
      setTemplates(r.data?.items || []);
      if (!activeKey && r.data?.items?.length) setActiveKey(r.data.items[0].key);
    } catch { toast.error('Could not load templates'); }
    finally { setFetching(false); }
  }, [activeKey]);

  const loadOne = useCallback(async (key) => {
    try {
      const r = await adminApi.get(`/admin/email-templates/${key}`);
      setTpl(r.data);
      setSubject(r.data.subject || '');
      setPlainBody(r.data.plain_body || '');
      setHtmlBody(r.data.html_body || '');
      setPreview(null);
      setSample(r.data.sample_defaults || {});
    } catch { toast.error('Could not load template'); }
  }, []);

  const loadPalette = useCallback(async () => {
    try {
      const r = await adminApi.get('/admin/email-brand');
      setPalette(r.data);
    } catch { /* non-fatal — editor still works */ }
  }, []);

  useEffect(() => { if (admin) { refreshList(); loadPalette(); } }, [admin, refreshList, loadPalette]);
  useEffect(() => { if (activeKey) loadOne(activeKey); }, [activeKey, loadOne]);

  if (loading) return null;
  if (!admin) return <Navigate to="/admin/login" replace />;

  const save = async () => {
    setSaving(true);
    try {
      await adminApi.put(`/admin/email-templates/${activeKey}`, { subject, plain_body: plainBody, html_body: htmlBody });
      toast.success('Template saved');
      await Promise.all([refreshList(), loadOne(activeKey)]);
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Save failed');
    } finally { setSaving(false); }
  };

  const resetDefault = async () => {
    if (!window.confirm('Restore this template to the built-in default? Your customizations will be lost.')) return;
    setResetting(true);
    try {
      await adminApi.post(`/admin/email-templates/${activeKey}/reset`);
      toast.success('Template reset to default');
      await Promise.all([refreshList(), loadOne(activeKey)]);
    } catch { toast.error('Reset failed'); }
    finally { setResetting(false); }
  };

  const runPreview = async () => {
    setPreviewing(true);
    try {
      const r = await adminApi.post(`/admin/email-templates/${activeKey}/preview`, { sample });
      setPreview(r.data);
    } catch { toast.error('Preview failed'); }
    finally { setPreviewing(false); }
  };

  const copyVar = async (v) => {
    try { await navigator.clipboard.writeText(`{{${v}}}`); toast.success(`{{${v}}} copied`); }
    catch { /* noop */ }
  };

  const savePalette = async () => {
    setSavingPalette(true);
    try {
      const r = await adminApi.put('/admin/email-brand', {
        accent_color: palette.accent_color,
        gradient_from: palette.gradient_from,
        gradient_to: palette.gradient_to,
        button_color: palette.button_color,
        text_on_accent: palette.text_on_accent,
        logo_enabled: palette.logo_enabled,
        logo_width_px: Number(palette.logo_width_px) || 140,
      });
      setPalette(r.data);
      toast.success('Brand palette saved');
      // Re-preview so admin sees the result immediately
      if (preview) await runPreview();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Palette save failed');
    } finally { setSavingPalette(false); }
  };

  const onLogoPick = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const fd = new FormData();
    fd.append('file', f);
    setUploadingLogo(true);
    try {
      const r = await adminApi.post('/admin/email-brand/logo', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setPalette(r.data);
      toast.success('Logo uploaded');
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Upload failed');
    } finally {
      setUploadingLogo(false);
      e.target.value = '';
    }
  };

  const clearLogo = async () => {
    try {
      const r = await adminApi.post('/admin/email-brand/logo-clear');
      setPalette(r.data);
      toast.success('Logo removed');
    } catch { toast.error('Remove failed'); }
  };

  return (
    <div data-testid="admin-email-templates-page" className="min-h-screen"
      style={{ background: 'radial-gradient(circle at 0% 0%, #1e293b 0%, #0f172a 50%, #020617 100%)' }}>
      <header className="border-b border-slate-800 bg-slate-950/60 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center gap-3">
          <Link to="/admin" className="w-9 h-9 rounded-lg bg-slate-900 border border-slate-800 flex items-center justify-center hover:bg-slate-800" data-testid="templates-back">
            <ArrowLeft className="w-4 h-4 text-slate-300" />
          </Link>
          <div className="w-9 h-9 rounded-lg bg-amber-500/10 border border-amber-500/30 flex items-center justify-center">
            <FileText className="w-4 h-4 text-amber-400" />
          </div>
          <div className="flex-1">
            <div className="text-white font-semibold">Email Templates</div>
            <div className="text-xs text-slate-400">Edit subject, body, brand palette and logo — changes apply to every outgoing email.</div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-6 space-y-5">
        {/* Brand palette (global) */}
        <section data-testid="email-brand-palette" className="bg-slate-900/70 border border-slate-800 rounded-xl p-5">
          <div className="flex items-start justify-between mb-3 gap-3 flex-wrap">
            <div>
              <div className="flex items-center gap-2 text-white font-semibold">
                <Palette className="w-4 h-4 text-amber-400" /> Email Brand Palette & Logo
              </div>
              <p className="text-[11px] text-slate-400 mt-1">
                Colors + logo auto-apply to every email via <code className="text-amber-300">{"{{accent_color}}"}</code>,{' '}
                <code className="text-amber-300">{"{{gradient_from}}"}</code>, <code className="text-amber-300">{"{{logo_html}}"}</code> etc. One place for the brand look.
              </p>
            </div>
            <Button onClick={savePalette} disabled={!palette || savingPalette} size="sm"
              data-testid="palette-save" className="bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold">
              {savingPalette ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Save className="w-3.5 h-3.5 mr-1.5" />}
              Save palette
            </Button>
          </div>
          {!palette ? (
            <div className="text-slate-400 text-sm flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
          ) : (
            <>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                {PALETTE_FIELDS.map((f) => (
                  <div key={f.key}>
                    <label className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">{f.label}</label>
                    <div className="mt-1 flex items-center gap-1">
                      <input
                        type="color"
                        value={palette[f.key] || '#000000'}
                        onChange={(e) => setPalette({ ...palette, [f.key]: e.target.value })}
                        data-testid={`palette-${f.key}-picker`}
                        className="w-10 h-9 rounded-md border border-slate-700 bg-slate-800 cursor-pointer"
                      />
                      <Input
                        value={palette[f.key] || ''}
                        onChange={(e) => setPalette({ ...palette, [f.key]: e.target.value })}
                        data-testid={`palette-${f.key}-hex`}
                        className="bg-slate-800 border-slate-700 text-white font-mono text-xs"
                      />
                    </div>
                    <p className="text-[10px] text-slate-500 mt-0.5">{f.hint}</p>
                  </div>
                ))}
              </div>

              {/* Logo controls */}
              <div className="mt-5 pt-4 border-t border-slate-800 grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="md:col-span-1">
                  <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-slate-400 font-semibold">
                    <ImageIcon className="w-3.5 h-3.5" /> Logo in email header
                  </div>
                  <label className="flex items-center gap-2 mt-2 text-sm text-slate-200">
                    <input
                      type="checkbox"
                      checked={!!palette.logo_enabled}
                      onChange={(e) => setPalette({ ...palette, logo_enabled: e.target.checked })}
                      data-testid="palette-logo-enabled"
                    />
                    Show logo above the gradient banner
                  </label>
                  <div className="mt-3">
                    <label className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">Logo width (px)</label>
                    <Input
                      type="number" min="40" max="400"
                      value={palette.logo_width_px || 140}
                      onChange={(e) => setPalette({ ...palette, logo_width_px: e.target.value })}
                      className="bg-slate-800 border-slate-700 text-white font-mono text-xs mt-1 w-32"
                    />
                  </div>
                </div>
                <div className="md:col-span-2">
                  <label className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">Current logo</label>
                  <div className="mt-1 bg-white rounded-md border border-slate-700 p-4 flex items-center justify-center min-h-[90px]">
                    {palette.logo_url ? (
                      <img src={palette.logo_url} alt="logo"
                        style={{ maxWidth: `${palette.logo_width_px || 140}px`, maxHeight: '60px', height: 'auto' }}
                        data-testid="palette-logo-img" />
                    ) : (
                      <div className="text-slate-500 text-xs">No logo uploaded — enable + upload below.</div>
                    )}
                  </div>
                  <div className="mt-2 flex gap-2 flex-wrap">
                    <input ref={logoRef} type="file" accept=".png,.jpg,.jpeg,.gif,.webp,.svg,image/*"
                      onChange={onLogoPick} className="hidden"
                      data-testid="palette-logo-file" />
                    <Button onClick={() => logoRef.current?.click()} disabled={uploadingLogo} size="sm"
                      data-testid="palette-logo-upload" variant="outline"
                      className="border-slate-700 bg-slate-900 text-slate-200 hover:bg-slate-800">
                      {uploadingLogo ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Upload className="w-3.5 h-3.5 mr-1.5" />}
                      Upload (PNG/JPG/SVG, &lt;100&nbsp;KB)
                    </Button>
                    {palette.logo_url && (
                      <Button onClick={clearLogo} size="sm" variant="outline"
                        data-testid="palette-logo-remove"
                        className="border-rose-500/40 bg-rose-500/5 text-rose-200 hover:bg-rose-500/10">
                        <X className="w-3.5 h-3.5 mr-1.5" /> Remove logo
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            </>
          )}
        </section>

        <div className="grid grid-cols-12 gap-5">
          <aside className="col-span-12 md:col-span-3 space-y-2" data-testid="templates-list">
            {fetching && templates.length === 0 ? (
              <div className="bg-slate-900/70 border border-slate-800 rounded-xl p-6 flex items-center gap-2 text-slate-400">
                <Loader2 className="w-4 h-4 animate-spin" /> Loading…
              </div>
            ) : (
              templates.map((t) => (
                <button key={t.key} onClick={() => setActiveKey(t.key)}
                  data-testid={`template-item-${t.key}`}
                  className={`w-full text-left p-4 rounded-xl border transition ${
                    activeKey === t.key
                      ? 'bg-amber-500/10 border-amber-500/40'
                      : 'bg-slate-900/70 border-slate-800 hover:border-slate-700'
                  }`}>
                  <div className="flex items-center justify-between gap-2">
                    <div className="font-semibold text-white text-sm">{t.name}</div>
                    {t.is_default
                      ? <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-slate-800 text-slate-400">Default</span>
                      : <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-300 border border-emerald-500/30">Customized</span>}
                  </div>
                  <p className="text-[11px] text-slate-400 mt-1 leading-snug line-clamp-3">{t.description}</p>
                  <div className="text-[10px] text-slate-500 mt-2 font-mono">{t.key}</div>
                </button>
              ))
            )}
          </aside>

          <section className="col-span-12 md:col-span-9 space-y-4">
            {!tpl ? (
              <div className="bg-slate-900/70 border border-slate-800 rounded-xl p-10 text-center text-slate-400 text-sm">
                Select a template to edit.
              </div>
            ) : (
              <>
                {/* Header + chips */}
                <div className="bg-slate-900/70 border border-slate-800 rounded-xl p-5 flex flex-wrap items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="text-white font-semibold">{tpl.name}</div>
                    <div className="text-xs text-slate-400 mt-1 max-w-xl">{tpl.description}</div>

                    <div className="flex flex-wrap gap-1.5 mt-3">
                      {(tpl.content_variables || []).map((v) => (
                        <button key={v} onClick={() => copyVar(v)}
                          data-testid={`var-chip-${v}`}
                          className="font-mono text-[11px] px-2 py-0.5 rounded-md bg-slate-800 border border-slate-700 text-amber-300 hover:bg-slate-700 inline-flex items-center gap-1"
                          title="Content variable — editable sample below">
                          {`{{${v}}}`}
                          <Copy className="w-2.5 h-2.5 opacity-60" />
                        </button>
                      ))}
                    </div>

                    {(tpl.brand_tokens || []).length > 0 && (
                      <div className="flex flex-wrap items-center gap-1.5 mt-2">
                        <span className="text-[10px] uppercase tracking-wider text-slate-500">Brand tokens</span>
                        {(tpl.brand_tokens || []).map((v) => (
                          <span key={v}
                            className="font-mono text-[10px] px-2 py-0.5 rounded-md bg-slate-800/50 border border-slate-700 text-sky-300"
                            title="Driven by Brand Palette — not editable here">
                            {`{{${v}}}`}
                          </span>
                        ))}
                        <span className="text-[10px] text-slate-500">· auto-filled from palette above</span>
                      </div>
                    )}
                  </div>
                  <div className="flex gap-2 flex-shrink-0">
                    <Button onClick={runPreview} disabled={previewing} variant="outline" size="sm"
                      data-testid="template-preview-btn"
                      className="border-slate-700 bg-slate-900 text-slate-200 hover:bg-slate-800">
                      {previewing ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Eye className="w-3.5 h-3.5 mr-1.5" />}
                      Preview
                    </Button>
                    <Button onClick={resetDefault} disabled={resetting} variant="outline" size="sm"
                      data-testid="template-reset-btn"
                      className="border-slate-700 bg-slate-900 text-slate-200 hover:bg-slate-800">
                      {resetting ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <RotateCcw className="w-3.5 h-3.5 mr-1.5" />}
                      Reset
                    </Button>
                    <Button onClick={save} disabled={saving} size="sm" data-testid="template-save-btn"
                      className="bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold">
                      {saving ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Save className="w-3.5 h-3.5 mr-1.5" />}
                      Save
                    </Button>
                  </div>
                </div>

                {/* Sample value editor */}
                <div className="bg-slate-900/70 border border-slate-800 rounded-xl p-5" data-testid="sample-values-panel">
                  <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
                    <div>
                      <div className="text-xs uppercase tracking-wider text-slate-400 font-semibold">Preview sample values</div>
                      <p className="text-[11px] text-slate-500">
                        Fill in what each variable should look like when you click Preview. Real emails use live data from each order.
                      </p>
                    </div>
                    <Button
                      type="button" size="sm" variant="outline"
                      onClick={() => setSample(tpl.sample_defaults || {})}
                      className="border-slate-700 bg-slate-900 text-slate-200 hover:bg-slate-800"
                    >
                      <RotateCcw className="w-3.5 h-3.5 mr-1.5" /> Reset to defaults
                    </Button>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {(tpl.content_variables || []).filter((v) => !v.endsWith('_block')).map((v) => (
                      <div key={v}>
                        <label className="text-[10px] uppercase tracking-wider text-slate-500 font-mono">{v}</label>
                        <Input
                          value={sample[v] ?? ''}
                          onChange={(e) => setSample((s) => ({ ...s, [v]: e.target.value }))}
                          data-testid={`sample-${v}`}
                          placeholder={`e.g. ${(tpl.sample_defaults || {})[v] || v}`}
                          className="bg-slate-800 border-slate-700 text-white font-mono text-xs"
                        />
                      </div>
                    ))}
                  </div>
                </div>

                {/* Subject */}
                <div className="bg-slate-900/70 border border-slate-800 rounded-xl p-5 space-y-2">
                  <label className="text-[11px] uppercase tracking-wider text-slate-400 font-semibold">Subject line</label>
                  <Input
                    data-testid="template-subject" value={subject}
                    onChange={(e) => setSubject(e.target.value)}
                    placeholder="Your {{brand_name}} license is ready…"
                    className="bg-slate-800/50 border-slate-700 text-white font-mono"
                  />
                </div>

                {/* Bodies */}
                <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                  <div className="bg-slate-900/70 border border-slate-800 rounded-xl p-5 space-y-2">
                    <div className="flex items-center justify-between">
                      <label className="text-[11px] uppercase tracking-wider text-slate-400 font-semibold">Plain-text body</label>
                      <span className="text-[10px] text-slate-500">{plainBody.length.toLocaleString()} chars</span>
                    </div>
                    <textarea data-testid="template-plain-body" value={plainBody}
                      onChange={(e) => setPlainBody(e.target.value)} rows={18}
                      className="w-full bg-slate-950 border border-slate-700 rounded-md p-3 text-slate-200 font-mono text-xs focus:outline-none focus:border-amber-500/40" />
                  </div>
                  <div className="bg-slate-900/70 border border-slate-800 rounded-xl p-5 space-y-2">
                    <div className="flex items-center justify-between">
                      <label className="text-[11px] uppercase tracking-wider text-slate-400 font-semibold">HTML body</label>
                      <span className="text-[10px] text-slate-500">{htmlBody.length.toLocaleString()} chars</span>
                    </div>
                    <textarea data-testid="template-html-body" value={htmlBody}
                      onChange={(e) => setHtmlBody(e.target.value)} rows={18}
                      className="w-full bg-slate-950 border border-slate-700 rounded-md p-3 text-emerald-200 font-mono text-xs focus:outline-none focus:border-amber-500/40" />
                  </div>
                </div>

                {preview && (
                  <div className="bg-slate-900/70 border border-amber-500/30 rounded-xl p-5 space-y-3" data-testid="template-preview-panel">
                    <div className="flex items-center gap-2">
                      <Eye className="w-4 h-4 text-amber-400" />
                      <div className="text-white font-semibold">Live Preview</div>
                      <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-300 border border-amber-500/30">Your sample values · current brand palette</span>
                    </div>
                    <div className="flex items-center gap-2 p-2 rounded-md bg-slate-950 border border-slate-800 text-sm">
                      <span className="text-[10px] uppercase tracking-wider text-slate-500">Subject</span>
                      <span className="text-white">{preview.subject}</span>
                    </div>
                    <div className="rounded-md bg-white overflow-auto max-h-[560px]" data-testid="template-preview-iframe">
                      <iframe title="email preview" className="w-full min-h-[520px]" srcDoc={preview.html_body} sandbox="" />
                    </div>
                    <div className="flex items-start gap-2 text-[11px] text-slate-400 p-2 rounded-md bg-slate-950/50 border border-slate-800">
                      <AlertTriangle className="w-3 h-3 mt-0.5 flex-shrink-0 text-amber-400" />
                      Preview reflects your <strong>unsaved</strong> sample values + the <strong>saved</strong> template body. Save the template first to see body edits here.
                    </div>
                  </div>
                )}

                <div className="flex items-center gap-2 text-[11px] text-slate-500 px-2">
                  <CheckCircle2 className="w-3 h-3 text-emerald-400" />
                  Use <code className="font-mono text-amber-300">{`{{variable}}`}</code> placeholders. Missing variables render as empty strings.
                </div>
              </>
            )}
          </section>
        </div>
      </main>
    </div>
  );
}
